import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BookOpen, Upload, Search, Download, FileText, Pencil, Eye, EyeOff, Loader2, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIAS = ["POP", "Doutrina", "Manual", "Legislação", "Complementar"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  titulo: "", numero: "", versao: "", data_doc: "", validade: "",
  categoria: "POP", orgao_origem: "", descricao: "",
  instrucao: "Instrução de Primeira Intervenção em Cenário de Crise",
});

export default function Biblioteca() {
  const { isAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [catFiltro, setCatFiltro] = useState("todas");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const q = isAdmin ? "?incluir_inativos=true" : "";
    api.get(`/biblioteca${q}`).then((r) => setDocs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setFile(null); setDlg(true); };
  const openEdit = (d) => {
    setForm({ titulo: d.titulo || "", numero: d.numero || "", versao: d.versao || "", data_doc: d.data_doc || "", validade: d.validade || "", categoria: d.categoria || "POP", orgao_origem: d.orgao_origem || "", descricao: d.descricao || "", instrucao: d.instrucao || "" });
    setEditId(d.id); setFile(null); setDlg(true);
  };

  const salvar = async () => {
    if (!form.titulo.trim()) { toast.error("Informe o título"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/biblioteca/${editId}`, form);
        if (file) {
          const fd = new FormData(); fd.append("file", file); fd.append("versao", form.versao || "");
          await api.post(`/biblioteca/${editId}/arquivo`, fd);
        }
        toast.success("Documento atualizado");
      } else {
        const fd = new FormData();
        if (file) fd.append("file", file);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        await api.post("/biblioteca", fd);
        toast.success("Documento adicionado");
      }
      setDlg(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleAtivo = async (d) => {
    try { await api.put(`/biblioteca/${d.id}`, { ativo: !d.ativo }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const excluir = async (id) => {
    if (!window.confirm("Remover este documento da biblioteca?")) return;
    try { await api.delete(`/biblioteca/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const baixar = async (d) => {
    if (!d.arquivo) { toast.info("Este documento ainda não possui arquivo anexado."); return; }
    try {
      const resp = await api.get(`/biblioteca/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.arquivo.original_filename || "documento"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Falha ao baixar o arquivo"); }
  };

  const filtrados = docs.filter((d) => {
    const okCat = catFiltro === "todas" || d.categoria === catFiltro;
    const okBusca = !busca || `${d.titulo} ${d.numero} ${d.descricao}`.toLowerCase().includes(busca.toLowerCase());
    return okCat && okBusca;
  });

  return (
    <div>
      <PageHeader title="Biblioteca / Material de Pesquisa" subtitle="Instrução de Primeira Intervenção em Cenário de Crise · consulta, estudo e fundamentação doutrinária">
        {isAdmin && <Button data-testid="bib-novo-btn" onClick={openNew}><Upload className="h-4 w-4 mr-2" /> Adicionar documento</Button>}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="bib-busca" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Pesquisar por título, número ou descrição..." className="pl-9" />
        </div>
        <Select value={catFiltro} onValueChange={setCatFiltro}>
          <SelectTrigger data-testid="bib-cat-filtro" className="w-full sm:w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="todas">Todas as categorias</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        : !filtrados.length ? (
          <Card className="p-8 text-center bg-card border-border">
            <BookOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">Nenhum documento encontrado.</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtrados.map((d) => (
              <Card key={d.id} data-testid={`bib-card-${d.id}`} className={`p-4 bg-card border-border flex flex-col ${!d.ativo ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-secondary shrink-0"><FileText className="h-5 w-5 text-sky-400" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.numero && <Badge className="bg-primary/15 text-primary border-primary/30">{d.numero}</Badge>}
                      <Badge className="bg-secondary text-secondary-foreground border-border">{d.categoria}</Badge>
                      {!d.institucional && <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30">Complementar</Badge>}
                      {!d.ativo && <Badge className="bg-red-500/15 text-red-300 border-red-500/30">Inativo</Badge>}
                    </div>
                    <div className="font-semibold mt-1.5 leading-snug">{d.titulo}</div>
                    {d.descricao && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{d.descricao}</p>}
                    {d.versao && <div className="text-[11px] text-muted-foreground font-mono mt-1">Versão {d.versao}{d.data_doc ? ` · ${d.data_doc}` : ""}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border">
                  <Button size="sm" variant="secondary" data-testid={`bib-download-${d.id}`} onClick={() => baixar(d)} className="flex-1">
                    <Download className="h-4 w-4 mr-1.5" /> {d.arquivo ? "Baixar" : "Sem arquivo"}
                  </Button>
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="ghost" data-testid={`bib-edit-${d.id}`} onClick={() => openEdit(d)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`bib-toggle-${d.id}`} onClick={() => toggleAtivo(d)}>{d.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                      <Button size="sm" variant="ghost" data-testid={`bib-del-${d.id}`} onClick={() => excluir(d.id)}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-400" /></Button>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editId ? "Editar documento" : "Adicionar documento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input data-testid="bib-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex.: POP 1.11 — Primeiro Interventor..." /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Número</Label><Input data-testid="bib-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} placeholder="POP 1.11" /></div>
              <div>
                <Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="bib-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Versão</Label><Input data-testid="bib-versao" value={form.versao} onChange={(e) => setForm({ ...form, versao: e.target.value })} /></div>
              <div><Label>Órgão de origem</Label><Input value={form.orgao_origem} onChange={(e) => setForm({ ...form, orgao_origem: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Data do documento</Label><Input value={form.data_doc} onChange={(e) => setForm({ ...form, data_doc: e.target.value })} placeholder="AAAA-MM-DD ou livre" /></div>
              <div><Label>Validade</Label><Input value={form.validade} onChange={(e) => setForm({ ...form, validade: e.target.value })} /></div>
            </div>
            <div><Label>Descrição</Label><Textarea data-testid="bib-descricao" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
            <div>
              <Label>Arquivo (PDF, imagem ou Word) {editId && "— opcional (substitui versão)"}</Label>
              <Input data-testid="bib-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button data-testid="bib-salvar" onClick={salvar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
